// frontend/src/pages/TransactionsIndex.tsx
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiTransactionsIndex, type Transaction } from "../lib/api";

const TABS: Record<string, string> = {
  all: "All",
  incoming: "Incoming",
  outgoing: "Outgoing",
  exchange: "Exchange",
  card: "Card",
  fee: "Fees",
};

function useQS() {
  const [sp, setSp] = useSearchParams();
  const get = (k: string, d = "") => sp.get(k) ?? d;
  const set = (patch: Record<string, string | number | null | undefined>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === null || String(v).length === 0) {
        next.delete(k);
      } else {
        next.set(k, String(v));
      }
    });
    setSp(next, { replace: true });
  };
  return { get, set, sp };
}

/** Keeps content clear of the fixed bottom nav on mobile (respects iOS safe area). */
function BottomSafeSpacer() {
  return (
    <div
      className="sm:hidden"
      style={{ height: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
    />
  );
}

/**
 * Mirror the Blade filter semantics on the client side so tabs behave
 * even if some rows still use legacy direction values.
 */
function applyFilter(rows: Transaction[], filter: string): Transaction[] {
  const f = (filter || "all").toLowerCase();
  if (f === "all") return rows;

  return rows.filter((tx) => {
    const type = (tx.type ?? "").toLowerCase();
    const dir = (tx.direction ?? "").toLowerCase();

    switch (f) {
      case "incoming":
        return dir === "incoming" || dir === "in";

      case "outgoing":
        return dir === "outgoing" || dir === "out";

      case "exchange":
        return type === "fx" || type === "fx_in" || type === "fx_out";

      case "card":
        return type === "card" || type === "card_charge";

      case "fee":
        return (
          type === "fee" ||
          type === "inbound_fee" ||
          type === "outbound_fee"
        );

      default:
        return true;
    }
  });
}

export default function TransactionsIndex() {
  const { get, set } = useQS();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<
    { currency: string; summary: any }[]
  >([]);
  const [meta, setMeta] = useState<any>(null);

  const filter = get("filter", "all");
  const search = get("search", "");
  const from = get("from", "");
  const to = get("to", "");
  const page = Number(get("page", "1"));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    apiTransactionsIndex({ filter: filter as any, search, from, to, page })
      .then((json) => {
        if (cancelled) return;
        setRows(json.data ?? []);
        setMeta(json.meta ?? null);
        if (Array.isArray(json.accounts)) {
          setAccounts(
            json.accounts.map((a: any) => ({
              currency: a.currency,
              summary: a.summary,
            }))
          );
        } else {
          setAccounts([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filter, search, from, to, page]);

  // ✅ Apply the same filter semantics as Blade on the client
  const filteredRows = useMemo(
    () => applyFilter(rows, filter),
    [rows, filter]
  );

  const grouped = useMemo(() => {
    // Group by 'Y-m-d' from created_at_fmt (server already provided)
    const map = new Map<string, Transaction[]>();
    for (const r of filteredRows) {
      const day = (r.ui?.created_at_fmt ?? "").slice(0, 10) || "—";
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(r);
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[0] < b[0] ? 1 : -1
    );
  }, [filteredRows]);

  const exportParams = useMemo(() => {
    const q = new URLSearchParams();
    if (filter) q.set("filter", filter);
    if (search) q.set("search", search);
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    return q.toString();
  }, [filter, search, from, to]);

  return (
    <div
      className="space-y-6 pb-6 sm:pb-6"
      style={{
        // Ensure the whole page has breathing room above the fixed bottom nav on mobile.
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
      }}
    >
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 leading-tight">
        Transaction History
      </h2>

      {/* Balance Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {accounts.map((a, i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm"
          >
            <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-1">
              {a.currency} Trust Funding
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Total Received:</strong>{" "}
              {a.summary.balance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {a.currency}
              <br />
              <strong>Pending Execution:</strong>{" "}
              {a.summary.pending_out.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {a.currency}
              <br />
              <strong>Queued for Trust Funding:</strong>{" "}
              <span className="text-green-700 dark:text-green-400">
                {a.summary.available.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                {a.currency}
              </span>
            </p>
          </div>
        ))}
      </div>

      {/* Filters / Search */}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {Object.entries(TABS).map(([key, label]) => (
          <button
            key={key}
            className={
              "px-4 py-2 rounded border text-sm font-medium transition " +
              (filter === key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100 hover:text-blue-700")
            }
            onClick={() => set({ filter: key, page: 1 })}
          >
            {label}
          </button>
        ))}

        <div className="ml-auto flex flex-wrap gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => set({ from: e.target.value, page: 1 })}
            className="px-3 py-2 border rounded text-sm"
            placeholder="From"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => set({ to: e.target.value, page: 1 })}
            className="px-3 py-2 border rounded text-sm"
            placeholder="To"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => set({ search: e.target.value, page: 1 })}
            className="px-3 py-2 border rounded text-sm"
            placeholder="Search description/type/currency"
          />
          <a
            className="btn"
            href={`/transactions/export?${exportParams}`}
            target="_blank"
            rel="noreferrer"
          >
            Export CSV
          </a>
          <a
            className="btn btn-white"
            href={`/transactions/export-pdf?${exportParams}`}
            target="_blank"
            rel="noreferrer"
          >
            Export PDF
          </a>
        </div>
      </div>

      {/* Table (desktop) */}
      <div className="mt-6 hidden sm:block overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
        <table className="min-w-full table-auto text-sm text-left">
          <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            <tr>
              <th className="px-4 py-2">Amount</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Recipient</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Created At</th>
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-gray-700">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6">
                  <div className="skeleton h-5 w-1/3 mb-2"></div>
                  <div className="skeleton h-5 w-2/3"></div>
                </td>
              </tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                >
                  No transactions yet.
                </td>
              </tr>
            )}
            {!loading &&
              filteredRows.map((tx) => (
                <tr
                  key={tx.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-4 py-2">
                    {tx.amount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    {tx.currency}
                  </td>
                  <td className="px-4 py-2">
                    {tx.ui?.type_label ?? tx.type}
                  </td>
                  <td className="px-4 py-2">
                    {tx.ui?.recipient_name ?? "—"}
                  </td>
                  <td className="px-4 py-2 capitalize">{tx.status}</td>
                  <td className="px-4 py-2">
                    {tx.ui?.created_at_fmt ?? "—"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && meta && meta.last_page > 1 && (
          <div className="mt-4 px-4 py-3 flex items-center justify_between">
            <button
              className="btn btn-white"
              disabled={meta.current_page <= 1}
              onClick={() =>
                set({ page: Math.max(meta.current_page - 1, 1) })
              }
            >
              Previous
            </button>
            <div className="text-sm text-gray-600">
              Page {meta.current_page} of {meta.last_page} • {meta.total} total
            </div>
            <button
              className="btn"
              disabled={meta.current_page >= meta.last_page}
              onClick={() =>
                set({
                  page: Math.min(meta.current_page + 1, meta.last_page),
                })
              }
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-4">
        {loading && (
          <div className="skeleton h-20 w-full rounded"></div>
        )}
        {!loading && filteredRows.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400">
            No transactions yet.
          </p>
        )}
        {!loading &&
          grouped.map(([date, items]) => (
            <div key={date}>
              <div className="text-xs text-gray-500 mb-2">
                {date}
              </div>
              {items.map((tx) => (
                <div
                  key={tx.id}
                  className="bg-white dark:bg-gray-800 p-4 rounded shadow text-sm"
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {tx.ui?.recipient_name ?? "—"}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">
                    {tx.ui?.type_label ?? tx.type} •{" "}
                    {tx.amount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    {tx.currency}
                  </div>
                  <div className="text-xs mt-1 text-gray-500">
                    {tx.ui?.created_at_fmt ?? "—"}
                  </div>
                  <div className="text-xs mt-1">
                    <strong>Status:</strong>{" "}
                    {tx.status?.[0]?.toUpperCase()}
                    {tx.status?.slice(1)}
                  </div>
                </div>
              ))}
            </div>
          ))}

        {/* Pagination (mobile) */}
        {!loading && meta && meta.last_page > 1 && (
          <div className="mt-4 px-2 flex items-center justify-between">
            <button
              className="btn btn-white"
              disabled={meta.current_page <= 1}
              onClick={() =>
                set({ page: Math.max(meta.current_page - 1, 1) })
              }
            >
              Prev
            </button>
            <div className="text-sm text-gray-600">
              {meta.current_page}/{meta.last_page}
            </div>
            <button
              className="btn"
              disabled={meta.current_page >= meta.last_page}
              onClick={() =>
                set({
                  page: Math.min(meta.current_page + 1, meta.last_page),
                })
              }
            >
              Next
            </button>
          </div>
        )}

        {/* Spacer so last card/pagination sits above the fixed bottom menu */}
        <BottomSafeSpacer />
      </div>

      {/* Also add a spacer for desktop if your bottom bar appears on larger screens via PWA install */}
      <div className="hidden sm:block" style={{ height: 0 }} />
    </div>
  );
}
